import { useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, Paperclip } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { RESTAURANT_BY_ID } from "../../constants";
import { pendingDirectPurchases } from "../../lib/permissions";
import { timeAgo } from "../../lib/storage";
import CreateDirectPurchaseDialog from "./CreateDirectPurchaseDialog";

// Panel that surfaces lines where the vendor delivered fewer units than
// were ordered. The admin (or restaurant manager) can either ignore or
// convert each line into a new "direct purchase" request, which removes
// it from this list.
export default function PendingDirectPurchasePanel({
  requests,
  products,
  suppliers,
  user,
  onChanged,
}) {
  const productById = useMemo(() => {
    const m = new Map((products || []).map((p) => [p.id, p]));
    return m;
  }, [products]);

  const shortfalls = useMemo(
    () => pendingDirectPurchases(requests, productById),
    [requests, productById]
  );

  const [dialog, setDialog] = useState({ open: false, shortfall: null });

  if (shortfalls.length === 0) return null;

  return (
    <div className="mt-6" data-testid="pending-direct-panel">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={16} className="text-[#C4552B]" />
        <p className="text-[11px] tracking-eyebrow uppercase text-[#0B1F3A]/70">
          Pending direct purchase · {shortfalls.length}
        </p>
      </div>
      <div className="bg-white border border-[#E6E0D4] rounded-xl divide-y divide-[#EEE8DC] overflow-hidden">
        {shortfalls.map((s) => {
          const supplier = suppliers.find((v) => v.id === s.request.vendor);
          return (
            <div
              key={s.key}
              data-testid={`pending-row-${s.request.id}-${s.line.productId}`}
              className="flex items-center gap-3 md:gap-4 px-4 md:px-5 py-3"
            >
              {s.product?.image ? (
                <img
                  src={s.product.image}
                  alt=""
                  className="h-10 w-10 rounded object-cover shrink-0"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div className="h-10 w-10 rounded bg-[#F3EFE7] shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-[#0B1F3A]">
                    {s.product?.name || s.line.productName}
                  </p>
                  <Badge variant="outline" className="border-[#C4552B]/40 text-[#C4552B] bg-[#F7E4DB]">
                    -{s.missing} {s.line.unit || s.product?.unit}
                  </Badge>
                </div>
                <p className="text-xs text-[#0B1F3A]/60 mt-0.5">
                  {supplier?.name || "—"} · {RESTAURANT_BY_ID[s.request.restaurant]?.name}
                  {" · invoice "}{s.request.invoiceNumber || "—"} · confirmed {timeAgo(s.request.deliveryConfirmedAt)}
                </p>
              </div>
              <Button
                data-testid={`pending-convert-${s.request.id}-${s.line.productId}`}
                size="sm"
                onClick={() => setDialog({ open: true, shortfall: s })}
                className="bg-[#0B1F3A] hover:bg-[#11294A] text-white"
              >
                Direct purchase <ArrowRight size={14} className="ml-1" />
              </Button>
              {s.request.invoiceFile && (
                <a
                  data-testid={`pending-invoice-${s.request.id}-${s.line.productId}`}
                  href={s.request.invoiceFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={s.request.invoiceFileName || `invoice-${s.request.invoiceNumber || s.request.id}.pdf`}
                  title={`Open invoice ${s.request.invoiceNumber || ""}`.trim()}
                  className="h-8 w-8 rounded-md bg-white border border-[#E6E0D4] hover:border-[#0B1F3A]/60 text-[#0B1F3A] flex items-center justify-center transition-colors"
                >
                  <Paperclip size={14} />
                </a>
              )}
            </div>
          );
        })}
      </div>

      <CreateDirectPurchaseDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        shortfall={dialog.shortfall}
        suppliers={suppliers}
        user={user}
        onCreated={onChanged}
      />
    </div>
  );
}
